import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { TEAM_AVATARS } from './teamAvatars';

/* One source of truth for "what does each AI executive do" — surfaced via the ? icon everywhere. */
export const AGENT_BIOS = {
  rachel: {
    role: ['Coordinadora del equipo · Recepcionista IA', 'Team coordinator · AI Receptionist'],
    bio: ['Coordina a todo el equipo ejecutivo IA, prepara tu brief diario y convierte tus datos en prioridades claras. Nunca ejecuta nada sin tu aprobación.',
      'Coordinates the whole AI executive team, prepares your daily brief and turns your data into clear priorities. Never executes anything without your approval.'],
  },
  stan: {
    role: ['Lead Generation · Analista de ventas', 'Lead Generation · Sales analyst'],
    bio: ['Analiza tus ventas por plato, canal y hora, detecta oportunidades de ingresos y busca nuevos clientes y leads para tu restaurante.',
      'Analyzes your sales by dish, channel and hour, spots revenue opportunities and hunts new guests and leads for your restaurant.'],
  },
  eva: {
    role: ['Operaciones', 'Operations'],
    bio: ['Vigila la operación diaria: costes, mermas, horarios y salud de tus integraciones. Avisa antes de que un problema se convierta en pérdida.',
      'Guards daily operations: costs, waste, schedules and integration health. Warns you before a problem becomes a loss.'],
  },
  sonny: {
    role: ['Social Media · Distribución', 'Social Media · Distribution'],
    bio: ['Prepara y distribuye tu contenido en redes y canales, con copies listos para aprobar. Nada se publica sin tu OK.',
      'Prepares and distributes your content across social channels, with ready-to-approve copy. Nothing goes out without your OK.'],
  },
  penny: {
    role: ['SEO y contenido · Marketing', 'SEO Content · Marketing'],
    bio: ['Crea tu marketing: campañas, promociones y contenido optimizado para que te encuentren en Google y en búsquedas con IA.',
      'Creates your marketing: campaigns, promotions and content optimized so guests find you on Google and AI search.'],
  },
  linda: {
    role: ['Legal y finanzas', 'Legal & finance'],
    bio: ['Mide el impacto financiero de cada acción y cuida el cumplimiento: contratos, licencias y letra pequeña, explicada en claro.',
      'Measures the financial impact of every action and watches compliance: contracts, licenses and fine print, explained clearly.'],
  },
};

export const TERM_DEFS = {
  roas: {
    label: ['Retorno publicitario (ROAS)', 'Ad return (ROAS)'],
    def: ['Por cada dólar invertido en marketing, cuántos dólares vuelven en ventas. Un ROAS de 4x significa $4 de ventas por cada $1 invertido. A más presupuesto, el retorno por dólar baja (rendimiento decreciente).',
      'For every dollar spent on marketing, how many dollars come back in sales. A 4x ROAS means $4 in sales per $1 spent. As budget grows, the return per dollar declines (diminishing returns).'],
  },
  food_cost: {
    label: ['Food cost (coste de producto)', 'Food cost'],
    def: ['Qué porcentaje del precio de un plato se va en ingredientes. Un food cost del 30% en un plato de $20 significa $6 de ingredientes. Entre 28–35% suele ser saludable en restauración.',
      'The share of a dish price spent on ingredients. A 30% food cost on a $20 dish means $6 of ingredients. 28–35% is generally healthy in restaurants.'],
  },
  uplift: {
    label: ['Uplift (crecimiento vs. antes)', 'Uplift (growth vs. before)'],
    def: ['Cuánto creció tu facturación comparada con tu ritmo previo a Kitxens, en porcentaje. Siempre es una estimación basada en tus datos importados.',
      'How much your revenue grew compared with your pre-Kitxens pace, as a percentage. Always an estimate based on your imported data.'],
  },
  contribution: {
    label: ['Margen de contribución', 'Contribution margin'],
    def: ['Lo que queda de cada venta extra tras descontar ingredientes y costes variables. Es mayor que el margen neto porque los costes fijos ya están cubiertos.',
      'What remains from each incremental sale after ingredients and variable costs. Higher than net margin because fixed costs are already covered.'],
  },
};

export function TermHelp({ term, isEs = true, dark = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const t = TERM_DEFS[term];
  if (!t) return null;
  return (
    <span className={`relative inline-flex align-middle normal-case tracking-normal ${className}`} ref={ref}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={t.label[isEs ? 0 : 1]} data-testid={`term-help-${term}`}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${dark ? 'text-white/40 hover:text-[#F77F11]' : 'text-gray-300 hover:text-[#F77F11]'}`}>
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-72 max-w-[85vw] rounded-2xl border border-gray-200 bg-white shadow-xl p-4 text-left normal-case tracking-normal font-normal"
          data-testid={`term-help-pop-${term}`}>
          <p className="text-[12.5px] font-bold text-gray-900">{t.label[isEs ? 0 : 1]}</p>
          <p className="mt-1.5 text-[12px] leading-relaxed text-gray-600">{t.def[isEs ? 0 : 1]}</p>
          <p className="mt-2 text-[10.5px] text-gray-400">{isEs ? 'Del glosario Kitxens' : 'From the Kitxens glossary'}</p>
        </div>
      )}
    </span>
  );
}

export function AgentHelp({ agent, isEs = true, dark = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const meta = AGENT_BIOS[agent];
  const av = TEAM_AVATARS.find((a) => a.key === agent);
  if (!meta || !av) return null;
  return (
    <span className={`relative inline-flex align-middle ${className}`} ref={ref}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label={isEs ? `¿Qué hace ${av.name}?` : `What does ${av.name} do?`}
        data-testid={`agent-help-${agent}`}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${dark ? 'text-white/40 hover:text-[#F77F11]' : 'text-gray-300 hover:text-[#F77F11]'}`}>
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-72 max-w-[85vw] rounded-2xl border border-gray-200 bg-white shadow-xl p-4 text-left normal-case tracking-normal font-normal"
          data-testid={`agent-help-pop-${agent}`}>
          <div className="flex items-center gap-2.5">
            <img src={av.img} alt={av.name} className="w-10 h-10 rounded-full object-cover" />
            <div className="min-w-0">
              <p className="text-[13.5px] font-bold text-gray-900">{av.name}</p>
              <p className="text-[11px] font-semibold text-[#F77F11]">{meta.role[isEs ? 0 : 1]}</p>
            </div>
          </div>
          <p className="mt-2.5 text-[12px] leading-relaxed text-gray-600">{meta.bio[isEs ? 0 : 1]}</p>
          <a href={`/ai-agents-for-restaurants#${agent}`} target="_blank" rel="noreferrer" data-testid={`agent-help-bio-${agent}`}
            className="mt-2.5 inline-flex items-center gap-1 text-[11.5px] font-bold text-[#F77F11] hover:underline">
            {isEs ? 'Ver bio completa' : 'Full bio'} <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      )}
    </span>
  );
}
